import { avisarErro } from '@/components/common/Toast';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/utils/api';
import { getApiBase } from '@/config';
import { compartilharArquivo, downloadOrShareFile } from '@/utils/downloadFile';

export interface PdfJob {
  jobId: string;
  label: string;
  status: 'pending' | 'ready' | 'error';
  url?: string;
  filename?: string;
  /**
   * Quando a geração produz MAIS DE UM arquivo — hoje só "Caderno + folhas de
   * gabarito", que sai em dois porque têm destinos diferentes: o caderno vai à
   * copiadora em N cópias, as folhas vão uma por aluno (achado 49).
   *
   * O backend devolvia esta lista desde `e996465` e o front nunca a leu: baixava
   * `url`, que é o PRIMEIRO da lista, e em seguida dispensava o job. O caderno
   * chegava, as folhas ficavam no disco do servidor e ninguém sabia. Não foi
   * regressão — o campo nasceu sem consumidor, e o commit que o criou não tocou
   * em nenhum arquivo do `clientWeb`.
   */
  arquivos?: Array<{ url: string; nome: string }>;
  /** Quais já foram baixados, por índice — o job só se dispensa quando não sobra nenhum. */
  baixados?: number[];
  error?: string;
  startedAt: number;
}

interface PdfJobContextValue {
  jobs: PdfJob[];
  addJob: (jobId: string, label: string) => void;
  dismissJob: (jobId: string) => void;
  /** `modo: 'compartilhar'` (1.4): a folha de compartilhar do sistema, com o PDF como arquivo. */
  downloadJob: (jobId: string, indice?: number, modo?: 'baixar' | 'compartilhar') => Promise<void>;
  pendingCount: number;
  readyCount: number;
}

const PdfJobContext = createContext<PdfJobContextValue | null>(null);

const POLL_INTERVAL = 3000;

export function PdfJobProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<PdfJob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // O SINO NA HORA (28/10/2026): o contador do sino só se busca a cada 60 s (`useContagemDeNaoLidas`), e o "PDF pronto"
  // ficava até um minuto sem número. A central é pedida de novo quando o trabalho NASCE (o "Gerando…") e quando deixa de
  // estar pendente — já, e 3 s depois: o servidor grava o aviso logo DEPOIS de responder ou de marcar o trabalho pronto
  // (`latexPdfJobStore.ts`, `emSegundoPlano`).
  const qc = useQueryClient();
  const avisarOSino = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['notificacoes'] });
    setTimeout(() => { void qc.invalidateQueries({ queryKey: ['notificacoes'] }); }, 3000);
  }, [qc]);

  const addJob = useCallback((jobId: string, label: string) => {
    setJobs(prev => [...prev, { jobId, label, status: 'pending', startedAt: Date.now() }]);
    avisarOSino();
  }, [avisarOSino]);

  const dismissJob = useCallback((jobId: string) => {
    setJobs(prev => prev.filter(j => j.jobId !== jobId));
  }, []);

  /**
   * Baixa UM arquivo do job. `indice` escolhe qual, quando há mais de um.
   *
   * O job só é dispensado quando todos os arquivos foram baixados — antes ele
   * sumia no primeiro download, o que era inofensivo enquanto todo job tinha um
   * arquivo só, e apagava as folhas de gabarito quando passou a ter dois.
   */
  /*
   * ══════════════════════════════════════════════════════════════════════════
   * O CLIQUE QUE NAO FAZ NADA
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Em 01/09/2026, vespera da aplicacao: "cliquei e nada aconteceu. Sem
   * mensagem de erro, sem aviso, o arquivo simplesmente nao baixou."
   *
   * Esta funcao tinha TRES saidas mudas — dois `return` e um `catch` que so
   * fazia `console.error`. Qualquer uma delas produz exatamente esse sintoma, e
   * nenhuma diz qual foi.
   *
   * E o achado 126 outra vez, no caminho mais caro: o do arquivo que a pessoa
   * precisa imprimir hoje. La eram seis telas trocando diagnostico por frase
   * fixa; aqui e pior — nao ha nem frase.
   *
   * O que NAO era, e vale registrar porque foi a primeira hipotese: nao e
   * bloqueador de pop-up. O navegador baixa por `<a download>` com um blob
   * (`downloadInBrowser`), nao por `window.open` — nao ha janela a bloquear.
   */
  const downloadJob = useCallback(async (jobId: string, indice = 0, modo: 'baixar' | 'compartilhar' = 'baixar') => {
    // Baixar por qualquer lugar marca o aviso do sino como lido (25/10/2026). Nunca segura o download.
    void api.post(`/api/notificacoes/pdf/${jobId}/lido`).catch(() => undefined);
    const job = jobs.find(j => j.jobId === jobId);
    if (!job) {
      // O aviso vive na memoria da aba: recarregar a pagina o perde. Sem isto,
      // o botao de um job que ja nao existe seria um clique morto.
      avisarErro('Este arquivo nao esta mais disponivel nesta aba. Gere o documento de novo.');
      return;
    }
    const lista = job.arquivos?.length ? job.arquivos : (job.url ? [{ url: job.url, nome: job.filename || 'simulado.pdf' }] : []);
    const alvo = lista[indice];
    if (!alvo) {
      avisarErro('Nao encontrei este arquivo no pedido. Gere o documento de novo.');
      return;
    }

    try {
      const base = getApiBase();
      const fullUrl = alvo.url.startsWith('http') ? alvo.url : (base ? base.replace(/\/$/, '') + alvo.url : alvo.url);
      const res = await api.get(fullUrl, { responseType: 'blob' });
      const arquivo = { blob: res.data, filename: alvo.nome || 'simulado.pdf', mimeType: 'application/pdf' };
      if (modo === 'compartilhar') await compartilharArquivo({ ...arquivo, titulo: job.label }); else await downloadOrShareFile(arquivo);
      const jaBaixados = [...new Set([...(job.baixados ?? []), indice])];
      if (jaBaixados.length >= lista.length) {
        dismissJob(jobId);
      } else {
        setJobs(prev => prev.map(j => (j.jobId === jobId ? { ...j, baixados: jaBaixados } : j)));
      }
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      /*
       * A mensagem do SERVIDOR, quando houver. O corpo de erro desta rota vem
       * como BLOB — foi pedido `responseType: 'blob'` —, entao o texto precisa
       * ser lido de volta antes de aparecer: sem isto, o "Link invalido ou
       * expirado" do servidor chegaria como `[object Blob]`.
       */
      let doServidor = '';
      const corpo = (err as { response?: { data?: unknown; status?: number } })?.response?.data;
      if (corpo instanceof Blob) {
        try { doServidor = JSON.parse(await corpo.text())?.error ?? ''; } catch { /* corpo nao-JSON */ }
      }
      const status = (err as { response?: { status?: number } })?.response?.status;
      avisarErro(
        doServidor
          ? `Nao foi possivel baixar: ${doServidor}`
          : status
            ? `Nao foi possivel baixar o arquivo (erro ${status}).`
            : 'Nao foi possivel baixar o arquivo. Verifique a conexao e tente de novo.'
      );
    }
  }, [jobs, dismissJob]);

  // Poll pending jobs
  useEffect(() => {
    const hasPending = jobs.some(j => j.status === 'pending');

    if (!hasPending) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      const pendingJobs = jobs.filter(j => j.status === 'pending');
      for (const job of pendingJobs) {
        try {
          const res = await api.get<{
            status: 'pending' | 'ready' | 'error';
            url?: string;
            arquivos?: Array<{ url: string; nome: string }>;
            filename?: string;
            error?: string;
            label?: string;
          }>(`/api/simulations/pdf-latex-job/${job.jobId}`);
          const data = res.data;

          if (data.status !== 'pending') {
            avisarOSino();
            // O caderno que não compilou diz a questão culpada: "[substituir:#id@simulado]" (`explicarFalhaDoCaderno`). Ela fica
            // guardada nesta sessão para o painel de impressão oferecer "gerar com a questão substituída por aviso"; a marca
            // sai do texto mostrado.
            let erro = data.error;
            const culpada = erro ? /\[substituir:#(\d+)@(\d+)\]/.exec(erro) : null;
            if (erro && culpada) {
              const rotulo = /na questão (\S+) \(#/.exec(erro)?.[1] ?? `#${culpada[1]}`;
              try { sessionStorage.setItem(`caderno-culpada:${culpada[2]}`, JSON.stringify({ questionId: Number(culpada[1]), rotulo })); } catch { /* sem armazenamento */ }
              erro = erro.replace(culpada[0], '').replace(/\s{2,}/g, ' ');
            }
            setJobs(prev => prev.map(j =>
              j.jobId === job.jobId
                ? { ...j, status: data.status, url: data.url, filename: data.filename, arquivos: data.arquivos, error: erro }
                : j
            ));
          }
        } catch {
          // Job may have expired — mark as error
          setJobs(prev => prev.map(j =>
            j.jobId === job.jobId
              ? { ...j, status: 'error', error: 'Job expirou ou não encontrado' }
              : j
          ));
        }
      }
    };

    // Poll immediately, then set interval
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobs.filter(j => j.status === 'pending').map(j => j.jobId).join(',')]);

  const pendingCount = jobs.filter(j => j.status === 'pending').length;
  const readyCount = jobs.filter(j => j.status === 'ready').length;

  return (
    <PdfJobContext.Provider value={{ jobs, addJob, dismissJob, downloadJob, pendingCount, readyCount }}>
      {children}
    </PdfJobContext.Provider>
  );
}

export function usePdfJobs() {
  const ctx = useContext(PdfJobContext);
  if (!ctx) throw new Error('usePdfJobs must be used within PdfJobProvider');
  return ctx;
}
